import { inject } from '@angular/core';
import { RedirectFunction, Router, UrlTree } from '@angular/router';
import { LayoutComponent } from './layouts/layout/layout.component';
import { VexRoutes } from '@vex/interfaces/vex-route.interface';
import { AuthGuard } from '@core/guards/auth.guard';
import { maintenanceDetailRoutes } from './pages/apps/maintenance/maintenance-detail/maintenance-detail.routes';
import { repairDetailRoutes } from './pages/apps/repairs/repair-detail/repair-detail.routes';
import { provideCockpitStore } from '@app/pages/apps/cockpit/store/cockpit-store.provider';
import { cockpitMockStorePreloadResolver } from '@app/pages/apps/cockpit/store/cockpit-mock-store.preload';
import { BuildingSyndicContextService } from './pages/apps/property-groups/building-detail/services/building-syndic-context.service';
import { provideMetersStore } from './pages/apps/meters';
import { RoleGuard } from '@core/guards/role.guard';
import { PropertyManagerGuard, PropertyManagerOrSyndicGuard, ServiceProviderGuard, SyndicWorkspaceGuard, AgencyWorkspaceGuard, TenantWorkspaceGuard } from '@core/guards/workspace-type.guard';
import { OnboardingCompletedGuard, AgencyOnboardingGuard } from '@core/guards/onboarding.guard';
import { EmailVerificationRequiredGuard } from '@core/guards/email-verification-required.guard';
import { adminDevGuard } from './admin-dev/admin-dev.guard';
import { ProfileTab, resolveProfileTab } from './pages/apps/user-profile/profile-tabs.util';

/**
 * Les anciens chemins de « Mon compte » (`/apps/user-profile/preferences`, …)
 * sont devenus des onglets. Ils redirigent vers le parent en portant leur
 * onglet dans `?tab=` — les query params déjà présents sont conservés, dont le
 * `?tab=settings` que des e-mails déjà envoyés transportent encore (il vise le
 * même écran, voir `resolveProfileTab`).
 */
const profileTabRedirect: (
  redirectData: Parameters<RedirectFunction>[0],
  tab: ProfileTab
) => UrlTree = (redirectData, tab) => {
  const incoming = redirectData.queryParams?.['tab'];
  // Un `?tab=` explicite l'emporte sur le chemin : un lien
  // `/notifications?tab=settings` ouvre les réglages, pas autre chose.
  const resolved = incoming ? resolveProfileTab(String(incoming)) : tab;
  return inject(Router).createUrlTree(['/apps/user-profile'], {
    queryParams: { ...redirectData.queryParams, tab: resolved }
  });
};

/**
 * Les quatre anciens écrans de « Paramètres de l'espace » (`/team`, `/billing`,
 * `/info`, `/profiles`) sont devenus des sections d'une seule page. Ils
 * redirigent vers elle avec leur section en fragment, query params conservés.
 */
const workspaceSettingsRedirect =
  (section: 'team' | 'billing' | 'info' | 'profiles'): RedirectFunction =>
  (redirectData) =>
    inject(Router).createUrlTree(['/apps/workspace/settings'], {
      queryParams: redirectData.queryParams,
      fragment: section
    });

export const appRoutes: VexRoutes = [
  // ========================================
  // PROGRESSIVE LOADING: AUTH SHELL ROUTES
  // ========================================
  // These routes load the lightweight auth shell first for fast initial loading
  {
    path: 'auth',
    loadComponent: () =>
      import('./auth-shell/auth-shell.component').then(
        (m) => m.AuthShellComponent
      ),
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./auth-shell/simple-login.component').then(
            (m) => m.SimpleLoginComponent
          )
      },
      {
        path: 'register',
        loadComponent: () =>
          import('./auth-shell/simple-register.component').then(
            (m) => m.SimpleRegisterComponent
          )
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./auth-shell/simple-forgot-password.component').then(
            (m) => m.SimpleForgotPasswordComponent
          )
      },
      {
        path: 'verify-email',
        loadComponent: () =>
          import('./pages/pages/auth/verify-email/verify-email.component').then(
            (m) => m.VerifyEmailComponent
          )
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./pages/pages/auth/reset-password/reset-password.component').then(
            (m) => m.ResetPasswordComponent
          )
      }
    ]
  },

  // ========================================
  // OWNER-WORKSPACE POST-SIGNUP ONBOARDING
  // ========================================
  // Full-screen, outside the main menu. AuthGuard runs inside the child
  // route so this stays lazy-loaded until the signup flow actually needs
  // it. Once `workspace.onboarding.completed` is true, OnboardingAccessGuard
  // bounces the user back to `/`.
  {
    path: 'onboarding',
    loadChildren: () =>
      import('./onboarding-shell/onboarding.routes').then(
        (m) => m.onboardingRoutes
      )
  },

  // ========================================
  // AGENCY POST-CREATION ONBOARDING (locked, full-screen)
  // ========================================
  // Declared OUTSIDE the main LayoutComponent (and BEFORE the `''` shell route
  // so it wins matching for `/agency/onboarding`) → the agency "configure your
  // agency" wizard renders full-screen with NO left menu, mirroring the
  // property-manager `/onboarding` lock. AgencyOnboardingGuard sends the user
  // through the shared user-profile step first (only the first connection) and
  // bounces an already-configured agency back to its dashboard.
  {
    path: 'agency/onboarding',
    canActivate: [AuthGuard, AgencyWorkspaceGuard, AgencyOnboardingGuard],
    loadComponent: () =>
      import('./pages/agency/onboarding-wizard/agency-onboarding-wizard.component').then(
        (m) => m.AgencyOnboardingWizardComponent
      ),
    title: 'routeTitles.setupAgency',
  },

  // ========================================
  // OPEN-BANKING CONSENT CALLBACK
  // ========================================
  // Where the bank sends the landlord back after they grant consent.
  //
  // Declared at the TOP LEVEL, outside the layout and its guards, and that is
  // the whole reason it exists as its own route: the user physically left the
  // application — they were at their bank, possibly on another device, possibly
  // for two minutes with a card reader — and they return to a cold page load.
  // Any guard that bounced them elsewhere would drop the query parameters the
  // provider appended, and with them the consent they just granted. It cannot
  // be re-granted without a second SCA.
  //
  // Authentication still applies where it counts: the page does nothing but
  // call `completeBankConnection`, which enforces workspace membership and the
  // owner/admin role server-side.
  {
    path: 'banking/callback',
    loadComponent: () =>
      import('./pages/apps/banking/banking-callback/banking-callback.component').then(
        (m) => m.BankingCallbackComponent
      )
  },

  // ========================================
  // TENANT INVITATION LANDING PAGE (Public)
  // ========================================
  // This is a public route for tenant invitation links
  {
    path: 'tenant/invitation',
    // Tenant-facing, so it wears the tenant palette even though it sits outside
    // the tenant shell — the colour must not flip at the moment someone first
    // meets the product. See core/theme/theme-scope.service.ts.
    data: { theme: 'tenant' },
    loadComponent: () =>
      import('./auth-shell/tenant-invitation/tenant-invitation-landing.component').then(
        (m) => m.TenantInvitationLandingComponent
      )
  },

  // ========================================
  // PROVIDER INVITATION LANDING PAGE (Public)
  // ========================================
  // Public route for service-provider claim links (`/provider/claim?token=…`).
  // Declared BEFORE the guarded `provider` shell so it wins route matching:
  // the invited provider lands here (accept + workspace switch) instead of
  // being bounced by ServiceProviderGuard into their personal workspace.
  {
    path: 'provider/claim',
    loadComponent: () =>
      import('./auth-shell/provider-invitation/provider-invitation-landing.component').then(
        (m) => m.ProviderInvitationLandingComponent
      )
  },

  // ========================================
  // AGENCY PUBLIC SELF-BOOKING (Public)
  // ========================================
  // Prospects book a property viewing from a shareable link: /book?token=…
  // No auth guard — the token is the only credential (resolved server-side).
  {
    path: 'book',
    loadComponent: () =>
      import('./pages/agency/self-booking/self-booking-landing.component').then(
        (m) => m.SelfBookingLandingComponent
      ),
    title: 'routeTitles.bookViewing',
  },

  // Owner opens their valuation report ("avis de valeur") from a tokenised
  // email link: /estimation?token=…  No auth guard — the token is the only
  // credential (resolved server-side by getPublicEstimation).
  {
    path: 'estimation/request',
    loadComponent: () =>
      import('./pages/agency/public-estimation/public-estimation-request.component').then(
        (m) => m.PublicEstimationRequestComponent
      ),
    title: 'routeTitles.estimationRequest',
  },
  {
    path: 'estimation',
    loadComponent: () =>
      import('./pages/agency/public-estimation/public-estimation.component').then(
        (m) => m.PublicEstimationComponent
      ),
    title: 'routeTitles.valuationReport',
  },

  // ========================================
  // RENT SIMULATOR (Public, behind an access code)
  // ========================================
  // No auth guard: prospects reach it with the shared access code, verified
  // server-side by `verifyAccessCode`. Signed-in users get the same page inside
  // the app shell at /tools/rent-simulator.
  {
    path: 'rent-simulator',
    loadComponent: () =>
      import('./pages/apps/rent-simulator/rent-simulator.component').then(
        (m) => m.RentSimulatorComponent
      ),
    title: 'routeTitles.rentSimulator',
  },

  // Co-owner opens a charge statement from a tokenised email link:
  // /charges/view?token=…  No auth guard — the token is the only credential
  // (resolved server-side, which also records the read receipt).
  // La personne confirme son numero WhatsApp depuis le lien du message de
  // bienvenue : /whatsapp/confirm?token=…  Aucune garde d'authentification —
  // le jeton est la seule preuve, resolu cote serveur.
  {
    path: 'whatsapp/confirm',
    loadComponent: () =>
      import('./auth-shell/whatsapp-confirm/whatsapp-confirm-landing.component').then(
        (m) => m.WhatsappConfirmLandingComponent
      ),
    title: 'routeTitles.confirmWhatsapp',
  },

  {
    path: 'charges/view',
    loadComponent: () =>
      import('./auth-shell/charge-statement-view/charge-statement-view-landing.component').then(
        (m) => m.ChargeStatementViewLandingComponent
      ),
    title: 'routeTitles.chargesStatement',
  },

  // Co-owner opens an AG convocation + RSVPs from a tokenised email link:
  // /assemblies/view?token=…  No auth guard — the token is the only credential
  // (resolved server-side, which also records the view receipt).
  {
    path: 'assemblies/view',
    loadComponent: () =>
      import('./auth-shell/assembly-convocation-view/assembly-convocation-view-landing.component').then(
        (m) => m.AssemblyConvocationViewLandingComponent
      ),
    title: 'routeTitles.assemblyNotice',
  },

  // Owner e-signs an agency mandate from a tokenised link: /sign-mandate?token=…
  // No auth guard — the token is the only credential (resolved server-side).
  {
    path: 'sign-mandate',
    loadComponent: () =>
      import('./pages/agency/mandate-signing/mandate-signing.component').then(
        (m) => m.MandateSigningComponent
      ),
    title: 'routeTitles.signMandate',
  },

  // ========================================
  // PUBLIC AGENCY WEBSITE — anonymous, slug-rendered, outside the Vex shell.
  // No auth guard; the public callables resolve everything by slug server-side.
  // ========================================
  {
    path: 'sites/:slug',
    loadChildren: () =>
      import('./pages/public-site/public-site.routes').then((m) => m.PUBLIC_SITE_ROUTES),
  },

  // ========================================
  // LEGAL DOCUMENTS — public, no auth (linked from the consent flow + footers)
  // ========================================
  {
    path: 'legal/terms',
    loadComponent: () =>
      import('./pages/legal/legal-page.component').then((m) => m.LegalPageComponent),
    data: { doc: 'terms' },
    title: 'routeTitles.terms',
  },
  {
    path: 'legal/privacy',
    loadComponent: () =>
      import('./pages/legal/legal-page.component').then((m) => m.LegalPageComponent),
    data: { doc: 'privacy' },
    title: 'routeTitles.privacy',
  },
  {
    path: 'legal/cookies',
    loadComponent: () =>
      import('./pages/legal/legal-page.component').then((m) => m.LegalPageComponent),
    data: { doc: 'cookies' },
    title: 'routeTitles.cookies',
  },
  {
    path: 'legal/legal-notice',
    loadComponent: () =>
      import('./pages/legal/legal-page.component').then((m) => m.LegalPageComponent),
    data: { doc: 'legal-notice' },
    title: 'routeTitles.legalNotice',
  },
  {
    path: 'legal/data-deletion',
    loadComponent: () =>
      import('./pages/legal/legal-page.component').then((m) => m.LegalPageComponent),
    data: { doc: 'data-deletion' },
    title: 'routeTitles.dataDeletion',
  },

  // ========================================
  // TENANT WORKSPACE — DEDICATED MOBILE-FIRST SHELL
  // ========================================
  // Tenants get their own standalone shell (header + bottom nav + PWA
  // install prompt), separate from the property-manager Vex layout.
  // AuthGuard runs here because we sit outside the main LayoutComponent
  // wrapper that normally enforces authentication.
  {
    path: 'tenant',
    canActivate: [AuthGuard, TenantWorkspaceGuard],
    // Claims the tenant palette for the whole portal. Children inherit it, so
    // this is the only place it is declared. See core/theme/theme-scope.service.ts.
    data: { theme: 'tenant' },
    loadComponent: () =>
      import('./pages/tenant/tenant-shell/tenant-shell.component').then(
        (m) => m.TenantShellComponent
      ),
    loadChildren: () =>
      import('./pages/tenant/tenant.routes').then((m) => m.TENANT_ROUTES)
  },

  // ========================================
  // LEGACY AUTH ROUTES (Redirect to Auth Shell)
  // ========================================
  // Redirect old auth routes to new auth shell for backward compatibility
  {
    path: 'login',
    redirectTo: 'auth/login',
    pathMatch: 'full'
  },
  {
    path: 'register',
    redirectTo: 'auth/register',
    pathMatch: 'full'
  },
  {
    path: 'forgot-password',
    redirectTo: 'auth/forgot-password',
    pathMatch: 'full'
  },
  {
    path: 'verify-email',
    redirectTo: 'auth/verify-email',
    pathMatch: 'full'
  },
  {
    path: 'reset-password',
    redirectTo: 'auth/reset-password',
    pathMatch: 'full'
  },
  // ========================================
  // EMAIL-VERIFICATION HARD-BLOCK (standalone)
  // ========================================
  // Full-screen wall for unverified accounts past the grace window. Lives at
  // the root level — OUTSIDE the auth-shell (which auto-redirects authenticated
  // users to `/`) and OUTSIDE LayoutComponent (whose guard redirects here) — so
  // neither ping-pongs with `EmailVerificationRequiredGuard`. AuthGuard keeps it
  // reachable only when signed in.
  {
    path: 'verify-required',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/pages/auth/verify-required/verify-required.component').then(
        (m) => m.VerifyRequiredComponent
      ),
    title: 'routeTitles.verifyRequired',
  },

  // Accepting a workspace invitation (#588). At the root, not under the app
  // shell: an invitee signs up with `skipPersonalWorkspace` and has no
  // workspace until this page accepts — the shell's OnboardingCompletedGuard
  // waited for one, timed out and sent them to /onboarding/welcome. Same URL
  // as before: invitation e-mails and the consent step point here.
  {
    path: 'apps/workspace/accept-invitation',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/apps/workspace/accept-invitation/accept-invitation.component').then(
        (m) => m.AcceptInvitationComponent
      ),
  },

  // Landing of a WhatsApp alert about a card (#545): the card's page with no
  // sidenav, toolbar or cockpit behind it. A tap from a phone should reach the
  // decision, not a feed to scroll past. Outside LayoutComponent, so it carries
  // the three app guards itself; the in-app bell and the push keep `/?card=`.
  {
    path: 'c/:cardId',
    canActivate: [AuthGuard, OnboardingCompletedGuard, EmailVerificationRequiredGuard, PropertyManagerOrSyndicGuard],
    loadComponent: () =>
      import('./pages/apps/cockpit/components/card-page/card-page.component').then(
        (m) => m.CardPageComponent
      ),
    providers: [...provideCockpitStore()],
    resolve: { cockpitDemoPreload: cockpitMockStorePreloadResolver },
    data: { cardLanding: true },
  },

  // Console d'administration v2 — réservée à une allowlist d'une adresse.
  //
  // DÉLIBÉRÉMENT HORS du bloc `path: ''` / LayoutComponent : elle n'hérite donc
  // ni du sidenav produit, ni de la toolbar, ni du fil d'Ariane, ni des trois
  // gardes de l'application (authentification, onboarding terminé, e-mail
  // vérifié). Une console d'administration n'a besoin d'aucun des trois, et la
  // largeur qu'ils consomment est celle qui manque à un tableau dense.
  //
  // `adminDevGuard` porte l'authentification lui-même (allowlist d'adresse +
  // claim `superadmin` sur un jeton frais) et renvoie SILENCIEUSEMENT vers
  // /admin dans tous les autres cas : rien ne doit signaler l'existence de la
  // zone pendant sa construction.
  //
  // L'admin v1 reste servi sur /admin, intact.
  {
    path: 'admin-dev',
    canActivate: [adminDevGuard],
    loadChildren: () =>
      import('./admin-dev/admin-dev.routes').then((m) => m.ADMIN_DEV_ROUTES),
  },

  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard, OnboardingCompletedGuard, EmailVerificationRequiredGuard],
    children: [
      {
        // The Cockpit home (#557) — mockup « 4 pistes », 1d/2d: a full page,
        // not the master–detail. The former flux keeps living on
        // /cockpit/flux below, and every `?card=` deep link goes there.
        path: '',
        canActivate: [PropertyManagerOrSyndicGuard],
        loadComponent: () =>
          import('./pages/apps/cockpit/home/cockpit-home.component').then(
            (m) => m.CockpitHomeComponent
          ),
        providers: [...provideCockpitStore()],
        resolve: { cockpitDemoPreload: cockpitMockStorePreloadResolver },
        data: {
          toolbarShadowEnabled: true,
          scrollDisabled: true,
          toolbarDensity: 'compact',
          breadcrumb: 'breadcrumbs.cockpit'
        }
      },
      {
        path: 'cockpit/flux',
        pathMatch: 'full',
        canActivate: [PropertyManagerOrSyndicGuard],
        loadComponent: () =>
          import('./pages/apps/cockpit/cockpit.component').then(
            (m) => m.CockpitComponent
          ),
        providers: [...provideCockpitStore()],
        resolve: { cockpitDemoPreload: cockpitMockStorePreloadResolver },
        data: {
          toolbarShadowEnabled: true,
          scrollDisabled: true,
          toolbarDensity: 'compact',
          breadcrumb: 'breadcrumbs.cockpit'
        }
      },
      {
        path: 'cards/:cardId/workflow',
        canActivate: [PropertyManagerOrSyndicGuard],
        // The dedicated page of every card (#581): stepper for a multi-step
        // card, the flux's decision surface full screen for a single-step one.
        loadComponent: () =>
          import('./pages/apps/cockpit/components/card-page/card-page.component').then(
            (m) => m.CardPageComponent
          ),
        providers: [...provideCockpitStore()],
        resolve: { cockpitDemoPreload: cockpitMockStorePreloadResolver },
        data: { scrollDisabled: true }
      },
      // Backward-compat: repair-detail, lifecycle emails and bookmarks use
      // /cockpit; the canonical Cockpit route is `/` (sidenav). A plain
      // `redirectTo: '/'` drops query params (same as /apps/cockpit today),
      // so use a RedirectFunction that rebuilds `/?card=…`. pathMatch full
      // so /cockpit/family/:type below is unaffected.
      {
        path: 'cockpit',
        pathMatch: 'full',
        redirectTo: ({ queryParams }) =>
          inject(Router).createUrlTree(['/cockpit/flux'], { queryParams })
      },
      {
        // Calendar of the cockpit's dated cards (#258) — redirects into the
        // Cockpit itself with its calendar pane active (#417), rather than
        // mounting CockpitCalendarComponent bare: a deep link from an e-mail or
        // a bookmark used to drop the reader on a fragment with none of the
        // Cockpit's own context (feed, filters, count). pathMatch full so
        // /cockpit/family/:type below is unaffected.
        path: 'cockpit/calendar',
        pathMatch: 'full',
        redirectTo: ({ queryParams }) =>
          inject(Router).createUrlTree(['/cockpit/flux'], { queryParams: { ...queryParams, pane: 'calendar' } })
      },
      {
        // Follow-ups the `CREATE_TASK` executor planned (#353) — same redirect
        // motive as `cockpit/calendar` above (#417). `result-ref.util` still
        // gets a real deep-link target; it now lands inside the Cockpit's tasks
        // pane instead of the standalone component. "Mes tâches" in the sidenav
        // remains a distinct screen — it reads a different source (derived
        // portfolio deadlines), this is only about the deep-link destination.
        path: 'cockpit/tasks',
        pathMatch: 'full',
        redirectTo: ({ queryParams }) =>
          inject(Router).createUrlTree(['/cockpit/flux'], { queryParams: { ...queryParams, pane: 'tasks' } })
      },
      {
        // Focused list of one cockpit family's in-progress cards — deep-link
        // target of the dashboard "En cours" tiles (indexations/notices/payments).
        // Redirects into the Cockpit's own embedded family panel (#417) instead
        // of mounting CockpitFamilyOverviewComponent bare.
        path: 'cockpit/family/:type',
        redirectTo: ({ params, queryParams }) =>
          inject(Router).createUrlTree(['/cockpit/flux'], { queryParams: { ...queryParams, family: params['type'] } })
      },
      // Financial Overview (formerly the home dashboard, served at /dashboard)
      {
        path: 'financial-dashboard',
        canActivate: [PropertyManagerGuard],
        loadComponent: () =>
          import('./pages/dashboards/property-dashboard/enhanced-property-dashboard.component').then(
            (m) => m.EnhancedPropertyDashboardComponent
          )
      },
      {
        // Les loyers impayes, tous contrats confondus (#396). Ils existaient
        // deja en cartes cockpit -- une par contrat, melees au reste -- ce qui
        // ne repondait pas a "qui me doit combien, et depuis quand".
        path: 'rent-arrears',
        canActivate: [PropertyManagerGuard],
        loadComponent: () =>
          import('./pages/apps/rent-arrears/rent-arrears.component').then(
            (m) => m.RentArrearsComponent
          )
      },
      // Backward-compat: keep old /dashboard links (emails, AI nav, bookmarks)
      // working after the rename to /financial-dashboard.
      {
        path: 'dashboard',
        redirectTo: 'financial-dashboard',
        pathMatch: 'full'
      },
      {
        // #411 : « Mes tâches » dérivait les mêmes données que le Cockpit
        // par une seconde chaîne client-side. Supprimé ; le lien est gardé
        // en redirection car l'URL est dans des favoris et des e-mails.
        path: 'my-tasks',
        redirectTo: '',
        pathMatch: 'full'
      },
      {
        // « Mise en route » — hub de complétude de l'espace de travail.
        // Première entrée du menu latéral, au-dessus du Cockpit.
        path: 'getting-started',
        canActivate: [PropertyManagerGuard],
        loadComponent: () =>
          import('./pages/apps/getting-started/getting-started.component').then(
            (m) => m.GettingStartedComponent
          ),
        data: {
          toolbarShadowEnabled: true,
          breadcrumb: 'breadcrumbs.gettingStarted'
        }
      },
      {
        path: 'apps',
        children: [
          {
            path: 'cockpit',
            redirectTo: '/',
            pathMatch: 'full'
          },
          {
            // Legacy deeplink: the Inbox moved under the Communications hub.
            // Redirect (preserving query params, e.g. ?msg=) so existing links
            // from AI tools, scheduler, toolbar and tenant detail keep working.
            path: 'inbox',
            redirectTo: 'communications/inbox',
            pathMatch: 'full'
          },
          {
            // Communications hub (#563): one menu entry, one page. `inbox`
            // carries channel tabs (e-mail / SMS / WhatsApp / other) over
            // Received · Scheduled · Sent, both axes in the query string.
            path: 'communications',
            data: {
              toolbarShadowEnabled: true
            },
            children: [
              {
                path: '',
                redirectTo: 'inbox',
                pathMatch: 'full'
              },
              {
                // Reçus — the inbound mailbox (former /apps/inbox).
                path: 'inbox',
                loadChildren: () => import('./pages/apps/inbox/inbox.routes'),
                data: {
                  toolbarShadowEnabled: true,
                  scrollDisabled: true,
                  // The lazy child is a bare `path: ''`, so this IS the leaf as
                  // far as the density walk is concerned — it has no sibling
                  // route to leak onto.
                  toolbarDensity: 'compact'
                }
              },
              {
                // Programmés and Envoyé used to be pages of their own; they are
                // views of the hub now (#563). Redirect, keeping the query
                // string so a bookmarked `?foo=` survives the move.
                path: 'scheduled',
                redirectTo: ({ queryParams }) =>
                  inject(Router).createUrlTree(['/apps/communications/inbox'], {
                    queryParams: { ...queryParams, view: 'scheduled' }
                  })
              },
              {
                path: 'sent',
                redirectTo: ({ queryParams }) =>
                  inject(Router).createUrlTree(['/apps/communications/inbox'], {
                    queryParams: { ...queryParams, view: 'sent' }
                  })
              },
              {
                // Non rattachés — les entrants WhatsApp qu'un numéro ambigu a
                // empêché d'attribuer. Sans cet écran, ils dormaient dans un bac
                // que seul un superadmin pouvait ouvrir, et chaque ambiguïté
                // devenait un ticket. Le callable ne montre que les
                // conversations dont une fiche de CET espace porte le numéro.
                path: 'unlinked',
                loadComponent: () =>
                  import(
                    './pages/apps/communications/whatsapp-unlinked-page.component'
                  ).then((m) => m.WhatsappUnlinkedPageComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              // Legacy 'pending' tab retired — draft approval is managed at the
              // cockpit card level. Redirect any bookmarked link to the outbox.
              {
                path: 'pending',
                redirectTo: ({ queryParams }) =>
                  inject(Router).createUrlTree(['/apps/communications/inbox'], {
                    queryParams: { ...queryParams, view: 'sent' }
                  })
              }
            ]
          },
          {
            path: 'documents',
            loadChildren: () => import('./pages/apps/documents/documents.routes'),
            data: {
              toolbarShadowEnabled: true
            }
          },
          {
            // La liste des notifications, sortie de « Mon compte » : c'est un
            // flux qu'on consulte, pas un réglage. Les réglages sont restés
            // dans le profil, sur `?tab=notifications`.
            path: 'notifications',
            loadComponent: () =>
              import('./pages/apps/notifications/notifications-list-page.component').then(
                (m) => m.NotificationsListPageComponent
              ),
            data: {
              toolbarShadowEnabled: true,
              breadcrumb: 'shared.notifications.title'
            }
          },
          {
            path: 'user-profile',
            loadComponent: () =>
              import('./pages/apps/user-profile/user-profile.component').then(
                (m) => m.UserProfileComponent
              ),
            // `hideOwnHeader` : les composants de section (préférences,
            // communication) laissent leur titre et leur description à la section.
            data: {
              toolbarShadowEnabled: true,
              hideOwnHeader: true
            },
            // Les quatre écrans sont devenus quatre sections de la page, visées
            // par `?tab=`. Les chemins d'origine survivent en redirections :
            // chaque footer d'e-mail lifecycle pointe sur `/communication`, et
            // ces liens-là sont déjà partis (voir `profile-tabs.util.ts` pour
            // les valeurs de `?tab=`, `settings` héritée comprise).
            children: [
              {
                path: 'profile',
                redirectTo: (r) => profileTabRedirect(r, 'profile'),
                pathMatch: 'full'
              },
              {
                path: 'preferences',
                redirectTo: (r) => profileTabRedirect(r, 'preferences'),
                pathMatch: 'full'
              },
              {
                path: 'notifications',
                redirectTo: (r) => profileTabRedirect(r, 'notifications'),
                pathMatch: 'full'
              },
              {
                path: 'communication',
                redirectTo: (r) => profileTabRedirect(r, 'communication'),
                pathMatch: 'full'
              }
            ]
          },
          {
            path: 'workspace',
            children: [
              {
                path: 'settings',
                loadComponent: () =>
                  import('./pages/apps/workspace/workspace-settings/workspace-settings.component').then(
                    (m) => m.WorkspaceSettingsComponent
                  ),
                data: {
                  toolbarShadowEnabled: true,
                  breadcrumb: 'breadcrumbs.workspaceSettings'
                },
                children: [
                  {
                    // One page, four sections (info, team, billing, profiles) — the
                    // former child screens stacked in `app-settings-section`s.
                    // `hideOwnHeader`: each embedded component drops its own
                    // subtitle, the section already carries it.
                    path: '',
                    loadComponent: () =>
                      import('./pages/apps/workspace/workspace-settings/workspace-settings-page/workspace-settings-page.component').then(
                        (m) => m.WorkspaceSettingsPageComponent
                      ),
                    data: { hideOwnHeader: true }
                  },
                  // The four former screens survive as redirects, with their section
                  // as the fragment: these URLs are in browser histories, bookmarks,
                  // e-mails already sent and the assistant's knowledge base.
                  { path: 'team', redirectTo: workspaceSettingsRedirect('team'), pathMatch: 'full' },
                  { path: 'billing', redirectTo: workspaceSettingsRedirect('billing'), pathMatch: 'full' },
                  { path: 'info', redirectTo: workspaceSettingsRedirect('info'), pathMatch: 'full' },
                  { path: 'profiles', redirectTo: workspaceSettingsRedirect('profiles'), pathMatch: 'full' },
                  {
                    // Rent reconciliation for ONE billing profile.
                    //
                    // It lives here, under the workspace settings, because the
                    // profiles it hangs off do: `workspaces/{id}/billing-profiles`.
                    // It used to sit under `apps/user/…`, which promised a
                    // per-user scope the data never had — and put the only
                    // entry point to open banking on a screen reachable solely
                    // from the toolbar dropdown, while the aside offered a
                    // second, identical profile list with no way in.
                    //
                    // Guarded where its sibling is not, and the asymmetry is
                    // deliberate: the profile LIST is settings every workspace
                    // type opens, this screen shows bank movements —
                    // property-manager territory. The syndic has its own under
                    // `syndic/building-banking`.
                    //
                    // Entitlement (paid plans) is NOT enforced here: it lives
                    // in the banking callables, so the screen can say what
                    // unlocks it instead of redirecting without a word
                    // (DESIGN.md §13).
                    path: 'profiles/:profileId/reconciliation',
                    canActivate: [PropertyManagerGuard],
                    loadComponent: () =>
                      import('./pages/apps/billing-profiles/billing-reconciliation/billing-reconciliation.component').then(
                        (m) => m.BillingReconciliationComponent
                      ),
                    data: {
                      toolbarShadowEnabled: true
                    }
                  },
                  {
                    path: 'inbox',
                    loadComponent: () =>
                      import('./pages/apps/workspace/workspace-settings/workspace-inbox/workspace-inbox.component').then(
                        (m) => m.WorkspaceInboxComponent
                      )
                  }
                ]
              }
            ]
          },
          {
            path: 'user',
            children: [
              // Both paths REDIRECT now. The screens they used to load were a
              // duplicate of `workspace/settings/profiles`: two components, two
              // stores, two models, one collection —
              // `workspaces/{id}/billing-profiles`, resolved identically by
              // `getWorkspaceCollection`. The `user/` prefix promised a
              // per-user scope the data never had.
              //
              // Kept as redirects rather than deleted: these URLs are in
              // browser histories, in bookmarks, and were the target of the
              // toolbar dropdown for a long time. A 404 would punish someone
              // for having used the product.
              {
                path: 'billing-profiles',
                redirectTo: '/apps/workspace/settings/profiles',
                pathMatch: 'full'
              },
              {
                path: 'billing-profiles/:profileId/reconciliation',
                redirectTo: '/apps/workspace/settings/profiles/:profileId/reconciliation'
              }
            ]
          }
        ]
      },
      {
        path: 'pages',
        children: [
          {
            path: 'error-404',
            loadComponent: () =>
              import('./pages/pages/errors/error-404/error-404.component').then(
                (m) => m.Error404Component
              )
          },
          {
            path: 'error-500',
            loadComponent: () =>
              import('./pages/pages/errors/error-500/error-500.component').then(
                (m) => m.Error500Component
              )
          }
        ]
      },
      {
        path: 'help',
        loadChildren: () => import('./pages/help/help.routes'),
        data: {
          toolbarShadowEnabled: true,
          breadcrumb: 'breadcrumbs.help'
        }
      },
      {
        path: 'property-evaluation',
        loadChildren: () =>
          import('./pages/apps/property-evaluation/property-evaluation.routes').then(
            m => m.propertyEvaluationRoutes
          ),
        data: {
          toolbarShadowEnabled: true
        }
      },
      {
        path: 'interventions',
        canActivate: [PropertyManagerOrSyndicGuard],
        loadChildren: () =>
          import('./pages/apps/interventions/interventions.routes').then(
            m => m.interventionsRoutes
          ),
        data: {
          toolbarShadowEnabled: true,
          breadcrumb: 'breadcrumbs.interventions'
        }
      },
      // Back-compat: keep /maintenance and /repairs reachable for old deep
      // links, bookmarks and transactional emails. New navigation always
      // routes through /interventions.
      {
        path: 'maintenance',
        canActivate: [PropertyManagerOrSyndicGuard],
        loadChildren: () =>
          import('./pages/apps/maintenance/maintenance.routes').then(m => m.maintenanceRoutes),
        data: {
          toolbarShadowEnabled: true,
          breadcrumb: 'breadcrumbs.maintenance'
        }
      },
      {
        path: 'repairs',
        canActivate: [PropertyManagerOrSyndicGuard],
        loadChildren: () =>
          import('./pages/apps/repairs/repairs.routes').then(m => m.repairsRoutes),
        data: {
          toolbarShadowEnabled: true,
          breadcrumb: 'breadcrumbs.repairs'
        }
      },
      {
        path: 'properties',
        canActivate: [PropertyManagerGuard],
        data: {
          breadcrumb: 'breadcrumbs.properties'
        },
        children: [
          {
            path: '',
            redirectTo: 'list',
            pathMatch: 'full'
          },
          {
            path: 'list',
            // No `breadcrumb` here: the parent `properties` route already declares
            // it. Declaring it again rendered "Biens · Biens" — a literal duplicate,
            // unrelated to the inheritance one BreadcrumbService now guards against.
            // Without it, `list` falls under SKIP_SEGMENTS and emits nothing.
            loadComponent: () => import('./pages/apps/properties/properties-grid/properties-grid.component').then(m => m.PropertiesGridComponent),
            data: {
              toolbarShadowEnabled: true,
              toolbarDensity: 'compact'
            }
          },
          {
            path: 'add',
            loadComponent: () => import('./pages/apps/properties/add-property/add-property.component').then(m => m.AddPropertyComponent),
            data: {
              toolbarShadowEnabled: true
            }
          },
          {
            path: ':id',
            children: [
              {
                path: '',
                redirectTo: 'overview',
                pathMatch: 'full'
              },
              {
                path: 'overview',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-overview/property-detail-overview.component').then(m => m.PropertyDetailOverviewComponent)
              },
              {
                path: 'financials',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-financials/property-detail-financials.component').then(m => m.PropertyDetailFinancialsComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'contracts',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-contracts/property-detail-contracts.component').then(m => m.PropertyDetailContractsComponent),
                data: {
                  toolbarShadowEnabled: true,
                  breadcrumb: 'breadcrumbs.sections.contracts'
                }
              },
              {
                path: 'documents',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-documents/property-detail-documents.component').then(m => m.PropertyDetailDocumentsComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'related-persons',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-related-persons/property-detail-related-persons.component').then(m => m.PropertyDetailRelatedPersonsComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'interventions',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-interventions/property-detail-interventions.component').then(m => m.PropertyDetailInterventionsComponent),
                data: {
                  toolbarShadowEnabled: true,
                  breadcrumb: 'breadcrumbs.interventions'
                }
              },
              {
                path: 'repairs',
                children: [
                  {
                    path: '',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-repairs/property-detail-repairs.component').then(m => m.PropertyDetailRepairsComponent),
                    data: {
                      toolbarShadowEnabled: true
                    }
                  },
                  {
                    path: 'create',
                    loadComponent: () => import('./pages/apps/repairs/repair-create/repair-create.component').then(m => m.RepairCreateComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      inPropertyContext: true
                    }
                  },
                  {
                    path: 'detail/:id',
                    loadComponent: () => import('./pages/apps/repairs/repair-detail/repair-detail.component').then(m => m.RepairDetailComponent),
                    children: repairDetailRoutes,
                    data: {
                      toolbarShadowEnabled: true,
                      inPropertyContext: true,
                      breadcrumb: { dynamic: true, param: 'id' }
                    }
                  }
                ],
                data: {
                  toolbarShadowEnabled: true,
                  breadcrumb: 'breadcrumbs.sections.repairs'
                }
              },
              {
                path: 'maintenance',
                children: [
                  {
                    path: '',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-maintenance/property-detail-maintenance.component').then(m => m.PropertyDetailMaintenanceComponent),
                    data: {
                      toolbarShadowEnabled: true
                    }
                  },
                  {
                    path: 'create',
                    loadComponent: () => import('./pages/apps/maintenance/maintenance-create/maintenance-create.component').then(m => m.MaintenanceCreateComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      inPropertyContext: true
                    }
                  },
                  {
                    path: 'detail/:id',
                    loadComponent: () => import('./pages/apps/maintenance/maintenance-detail/maintenance-detail.component').then(m => m.MaintenanceDetailComponent),
                    children: maintenanceDetailRoutes,
                    data: {
                      toolbarShadowEnabled: true,
                      inPropertyContext: true,
                      breadcrumb: { dynamic: true, param: 'id' }
                    }
                  }
                ],
                data: {
                  toolbarShadowEnabled: true,
                  breadcrumb: 'breadcrumbs.sections.maintenance'
                }
              },
              {
                path: 'keys',
                children: [
                  {
                    path: '',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-keys/property-detail-keys.component').then(m => m.PropertyDetailKeysComponent),
                    data: {
                      toolbarShadowEnabled: true
                    }
                  },
                  {
                    path: 'create',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-keys/pages/key-form-page.component').then(m => m.KeyFormPageComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      inPropertyContext: true
                    }
                  },
                  {
                    path: 'detail/:keyId',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-keys/pages/key-detail-page.component').then(m => m.KeyDetailPageComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      inPropertyContext: true,
                      breadcrumb: { dynamic: true, param: 'keyId' }
                    }
                  },
                  {
                    path: 'detail/:keyId/edit',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-keys/pages/key-form-page.component').then(m => m.KeyFormPageComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      inPropertyContext: true
                    }
                  }
                ],
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'details',
                data: {
                  breadcrumb: 'breadcrumbs.sections.details'
                },
                children: [
                  {
                    path: '',
                    redirectTo: 'characteristics',
                    pathMatch: 'full'
                  },
                  {
                    path: 'characteristics',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-details/property-detail-details.component').then(m => m.PropertyDetailDetailsComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      section: 'characteristics'
                    }
                  },
                  {
                    path: 'composition',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-details/property-detail-details.component').then(m => m.PropertyDetailDetailsComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      section: 'composition'
                    }
                  },
                  {
                    path: 'legal',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-details/property-detail-details.component').then(m => m.PropertyDetailDetailsComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      section: 'legal'
                    }
                  }
                ]
              },
              {
                path: 'building',
                data: {
                  breadcrumb: 'breadcrumbs.buildings'
                },
                children: [
                  {
                    path: '',
                    redirectTo: 'characteristics',
                    pathMatch: 'full'
                  },
                  {
                    path: 'characteristics',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-details/property-detail-details.component').then(m => m.PropertyDetailDetailsComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      section: 'building'
                    }
                  },
                  {
                    // Legacy: the standalone "Features" tab was merged into the
                    // building characteristics page (Equipment & Amenities card).
                    path: 'features',
                    redirectTo: 'characteristics',
                    pathMatch: 'full'
                  },
                  {
                    path: 'syndic',
                    loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-details/property-detail-details.component').then(m => m.PropertyDetailDetailsComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      section: 'building-syndic'
                    }
                  }
                ]
              },
              {
                path: 'certificates',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-certificates/property-detail-certificates.component').then(m => m.PropertyDetailCertificatesComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'notes',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-notes/property-detail-notes.component').then(m => m.PropertyDetailNotesComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'expenses',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-expenses/property-detail-expenses.component').then(m => m.PropertyDetailExpensesComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'meters',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-meters/property-detail-meters.component').then(m => m.PropertyDetailMetersComponent),
                providers: [
                  // Pattern B: route-scoped meters store (mock/firestore switchable via ?store=mock)
                  ...provideMetersStore()
                ],
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'photos',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-photos/property-detail-photos.component').then(m => m.PropertyDetailPhotosComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'settings',
                loadComponent: () => import('./pages/apps/properties/property-detail/property-detail-settings/property-detail-settings.component').then(m => m.PropertyDetailSettingsComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                // Ancienne URL de « Mise en route du bien » : les favoris, les
                // e-mails et les cartes du cockpit déjà émis y pointent encore.
                path: 'listing-quality',
                redirectTo: 'getting-started'
              },
              {
                path: 'getting-started',
                loadComponent: () => import('./pages/apps/properties/property-listing-quality/property-listing-quality.component').then(m => m.PropertyListingQualityComponent),
                data: {
                  toolbarShadowEnabled: true,
                  // Le libellé répète le titre de la barre : le toolbar retire
                  // alors lui-même le dernier maillon du fil d'Ariane.
                  breadcrumb: 'shared.contextual.property.propertySetup'
                }
              },
              {
                path: 'add-assembly',
                loadComponent: () => import('./pages/apps/properties/add-assembly-page/add-assembly-page.component').then(m => m.AddAssemblyPageComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
            ],
            loadComponent: () => import('./pages/apps/properties/property-detail/property-detail.component').then(m => m.PropertyDetailComponent),
            data: {
              toolbarShadowEnabled: true,
              breadcrumb: { dynamic: true, param: 'id' }
            }
          }
        ]
      },
      {
        path: 'tenants',
        canActivate: [PropertyManagerGuard],
        data: {
          breadcrumb: 'breadcrumbs.tenants'
        },
        children: [
          {
            path: '',
            redirectTo: 'list',
            pathMatch: 'full'
          },
          {
            path: 'list',
            loadComponent: () => import('./pages/apps/tenants/tenants-list/tenants-list.component').then(m => m.TenantsListComponent),
            data: {
              toolbarShadowEnabled: true,
              toolbarDensity: 'compact'
            }
          },
          {
            path: 'add',
            loadComponent: () => import('./pages/apps/tenants/add-tenant/add-tenant.component').then(m => m.AddTenantComponent),
            data: {
              toolbarShadowEnabled: true
            }
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./pages/apps/tenants/add-tenant/add-tenant.component').then(m => m.AddTenantComponent),
            data: {
              toolbarShadowEnabled: true,
              isEditMode: true
            }
          },
          {
            path: ':id',
            children: [
              {
                path: '',
                redirectTo: 'overview',
                pathMatch: 'full'
              },
              {
                path: 'overview',
                loadComponent: () => import('./pages/apps/tenants/tenant-detail/tenant-detail-overview/index').then(m => m.TenantDetailOverviewComponent)
              },
              {
                // Les contrats du locataire s'affichent dans l'Aperçu (#658).
                path: 'contracts',
                redirectTo: 'overview',
                pathMatch: 'full'
              },
              {
                path: 'documents',
                loadComponent: () => import('./pages/apps/tenants/tenant-detail/tenant-detail-documents').then(m => m.TenantDetailDocumentsComponent)
              },
              {
                // Onglet masqué (#658) : les notes restent en base, la page n'est plus proposée.
                path: 'notes',
                redirectTo: 'overview',
                pathMatch: 'full'
              },
              {
                path: 'activity',
                loadComponent: () => import('./pages/apps/tenants/tenant-detail/tenant-detail-activity/tenant-detail-activity.component').then(m => m.TenantDetailActivityComponent)
              }
            ],
            loadComponent: () => import('./pages/apps/tenants/tenant-detail/tenant-detail.component').then(m => m.TenantDetailComponent),
            data: {
              toolbarShadowEnabled: true,
              breadcrumb: { dynamic: true, param: 'id' }
            }
          }
        ]
      },
      {
        path: 'contracts',
        canActivate: [PropertyManagerGuard],
        data: {
          breadcrumb: 'breadcrumbs.contracts'
        },
        children: [
          {
            path: '',
            redirectTo: 'list',
            pathMatch: 'full'
          },
          {
            path: 'list',
            loadComponent: () => import('./pages/apps/contracts/contracts-list/contracts-list.component').then(m => m.ContractsListComponent),
            data: {
              toolbarShadowEnabled: true,
              toolbarDensity: 'compact'
            }
          },
          {
            path: 'add',
            loadComponent: () => import('./pages/apps/contracts/add-contract/add-contract.component').then(m => m.AddContractComponent),
            data: {
              toolbarShadowEnabled: true
            }
          },
          {
            // `/contracts/new` is what people type; the wizard lives on `add`.
            // Without this alias the URL 404s with no hint of the right one.
            path: 'new',
            redirectTo: 'add',
            pathMatch: 'full'
          },
          {
            path: 'detail/:id',
            loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail.component').then(m => m.ContractDetailComponent),
            children: [
              {
                path: '',
                redirectTo: 'overview',
                pathMatch: 'full'
              },
              {
                path: 'overview',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-overview/contract-detail-overview.component').then(m => m.ContractDetailOverviewComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'clauses',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-clauses/contract-detail-clauses.component').then(m => m.ContractDetailClausesComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'payments',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-payments/contract-detail-payments.component').then(m => m.ContractDetailPaymentsComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'indexations',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-indexations/contract-detail-indexations.component').then(m => m.ContractDetailIndexationsComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'communications',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-communications/contract-detail-communications.component').then(m => m.ContractDetailCommunicationsComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'registration',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-registration/contract-detail-registration.component').then(m => m.ContractDetailRegistrationComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'documents',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-documents/contract-detail-documents.component').then(m => m.ContractDetailDocumentsComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                // Onglet masqué (#653) : les notes restent en base, la page n'est plus proposée.
                path: 'notes',
                redirectTo: 'overview',
                pathMatch: 'full'
              },
              {
                path: 'settings',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-settings/contract-detail-settings.component').then(m => m.ContractDetailSettingsComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'activities',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-activities/contract-detail-activities.component').then(m => m.ContractDetailActivitiesComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'inventory',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-detail-inventory/contract-detail-inventory.component').then(m => m.ContractDetailInventoryComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              },
              {
                path: 'inventory/:reportId',
                loadComponent: () => import('./pages/apps/contracts/contract-detail/contract-inventory-editor/contract-inventory-editor.component').then(m => m.ContractInventoryEditorComponent),
                data: {
                  toolbarShadowEnabled: true
                }
              }
            ],
            data: {
              toolbarShadowEnabled: true,
              breadcrumb: { dynamic: true, param: 'id' }
            }
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./pages/apps/contracts/add-contract/add-contract.component').then(m => m.AddContractComponent),
            data: {
              toolbarShadowEnabled: true,
              isEditMode: true
            }
          }
        ]
      },
      {
        path: 'buildings',
        canActivate: [PropertyManagerGuard],
        data: {
          breadcrumb: 'breadcrumbs.buildings'
        },
        children: [
          {
            // Pas d'écran liste à part : les immeubles vivent dans la grille des
            // biens. Le crumb « Immeubles » de `/buildings/add` mène ici.
            path: '',
            redirectTo: '/properties/list',
            pathMatch: 'full'
          },
          {
            path: 'add',
            loadComponent: () => import('./pages/apps/buildings/add-building/add-building.component').then(m => m.AddBuildingComponent),
            data: {
              toolbarShadowEnabled: true
            }
          }
        ]
      },
      {
        path: 'building',
        // `PropertyManagerGuard`, et pas `PropertyManagerOrSyndicGuard` : les
        // deux « immeubles » du produit ne sont pas la même donnée. Celui-ci lit
        // `workspaces/{wid}/property_groups` (property-groups.service.ts), la
        // vue propriétaire. Le syndic a son propre arbre complet sous
        // `/syndic/buildings/:buildingId`, qui lit la collection `buildings` —
        // celle que firestore.rules décrit comme « SYNDIC-AUTHORED ».
        //
        // Sans garde, un espace prestataire ou agence chargeait l'écran et ses
        // 12 onglets pour n'y rien trouver, là où `/properties/:id` le renvoie
        // ailleurs. La frontière de données, elle, tenait déjà :
        // `property_groups` tombe sous la règle générique d'espace de travail
        // (`isWorkspaceMember`), donc rien n'a jamais fuité — c'est le
        // comportement qui était incohérent, pas l'accès.
        canActivate: [PropertyManagerGuard],
        children: [
          {
            // Le crumb « Immeuble » des fiches `/building/:id/…` mène ici.
            path: '',
            redirectTo: '/properties/list',
            pathMatch: 'full'
          },
          {
            path: ':id',
            loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail.component').then(m => m.BuildingDetailComponent),
            providers: [BuildingSyndicContextService],
            children: [
              {
                path: '',
                redirectTo: 'overview',
                pathMatch: 'full'
              },
              {
                path: 'overview',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-overview/building-detail-overview.component').then(m => m.BuildingDetailOverviewComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'characteristics',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-characteristics/building-detail-characteristics.component').then(m => m.BuildingDetailCharacteristicsComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'units',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-units/building-detail-units.component').then(m => m.BuildingDetailUnitsComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'meters',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-meters/building-detail-meters.component').then(m => m.BuildingDetailMetersComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'financials',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-financials/building-detail-financials.component').then(m => m.BuildingDetailFinancialsComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'maintenance',
                children: [
                  {
                    path: '',
                    loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-maintenance/building-detail-maintenance.component').then(m => m.BuildingDetailMaintenanceComponent),
                    data: {
                      toolbarShadowEnabled: false,
                      scrollDisabled: false,
                      containerEnabled: true
                    }
                  },
                  {
                    path: 'create',
                    loadComponent: () => import('./pages/apps/maintenance/maintenance-create/maintenance-create.component').then(m => m.MaintenanceCreateComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      inBuildingContext: true
                    }
                  },
                  {
                    path: 'detail/:id',
                    loadComponent: () => import('./pages/apps/maintenance/maintenance-detail/maintenance-detail.component').then(m => m.MaintenanceDetailComponent),
                    children: maintenanceDetailRoutes,
                    data: {
                      toolbarShadowEnabled: true,
                      inBuildingContext: true,
                      breadcrumb: { dynamic: true, param: 'id' }
                    }
                  }
                ],
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'repairs',
                children: [
                  {
                    path: '',
                    loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-repairs/building-detail-repairs.component').then(m => m.BuildingDetailRepairsComponent),
                    data: {
                      toolbarShadowEnabled: false,
                      scrollDisabled: false,
                      containerEnabled: true
                    }
                  },
                  {
                    path: 'create',
                    loadComponent: () => import('./pages/apps/repairs/repair-create/repair-create.component').then(m => m.RepairCreateComponent),
                    data: {
                      toolbarShadowEnabled: true,
                      inBuildingContext: true
                    }
                  },
                  {
                    path: 'detail/:id',
                    loadComponent: () => import('./pages/apps/repairs/repair-detail/repair-detail.component').then(m => m.RepairDetailComponent),
                    children: repairDetailRoutes,
                    data: {
                      toolbarShadowEnabled: true,
                      inBuildingContext: true,
                      breadcrumb: { dynamic: true, param: 'id' }
                    }
                  }
                ],
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'expenses',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-expenses/building-detail-expenses.component').then(m => m.BuildingDetailExpensesComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'charges',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-charges/building-detail-charges.component').then(m => m.BuildingDetailChargesComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'documents',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-documents/building-detail-documents.component').then(m => m.BuildingDetailDocumentsComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'notes',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-notes/building-detail-notes.component').then(m => m.BuildingDetailNotesComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'certificates',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-certificates/building-detail-certificates.component').then(m => m.BuildingDetailCertificatesComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              },
              {
                path: 'settings',
                loadComponent: () => import('./pages/apps/property-groups/building-detail/building-detail-settings/building-detail-settings.component').then(m => m.BuildingDetailSettingsComponent),
                data: {
                  toolbarShadowEnabled: false,
                  scrollDisabled: false,
                  containerEnabled: true
                }
              }
            ],
            data: {
              toolbarShadowEnabled: true,
              breadcrumb: { dynamic: true, param: 'id' }
            }
          }
        ]
      },
      {
        path: 'tools/indexation-calculator',
        canActivate: [PropertyManagerGuard],
        loadComponent: () =>
          import('./pages/apps/indexation-calculator/indexation-calculator.component').then(
            m => m.IndexationCalculatorComponent
          ),
        data: {
          breadcrumb: 'breadcrumbs.indexationCalculator',
          toolbarShadowEnabled: false,
          scrollDisabled: false,
          containerEnabled: true,
          toolbarDensity: 'compact'
        }
      },
      {
        path: 'tools/rent-simulator',
        canActivate: [PropertyManagerGuard],
        loadComponent: () =>
          import('./pages/apps/rent-simulator/rent-simulator.component').then(
            m => m.RentSimulatorComponent
          ),
        data: {
          breadcrumb: 'breadcrumbs.rentSimulator',
          embedded: true,
          toolbarShadowEnabled: false,
          scrollDisabled: false,
          containerEnabled: true,
          toolbarDensity: 'compact'
        }
      },
      {
        path: 'loans',
        canActivate: [PropertyManagerGuard],
        data: {
          breadcrumb: 'breadcrumbs.loans'
        },
        children: [
          {
            path: '',
            redirectTo: 'list',
            pathMatch: 'full'
          },
          {
            path: 'list',
            loadComponent: () => import('./pages/apps/loans/loans-list/loans-list.component').then(m => m.LoansListComponent),
            data: {
              toolbarShadowEnabled: false,
              scrollDisabled: false,
              containerEnabled: true,
              toolbarDensity: 'compact'
            }
          },
          {
            path: 'detail/:id',
            loadComponent: () => import('./pages/apps/loans/loan-detail/loan-detail.component').then(m => m.LoanDetailComponent),
            children: [
              {
                path: '',
                redirectTo: 'overview',
                pathMatch: 'full'
              },
              {
                path: 'overview',
                loadComponent: () => import('./pages/apps/loans/loan-detail/loan-detail-overview/loan-detail-overview.component').then(m => m.LoanDetailOverviewComponent)
              },
              {
                path: 'amortization',
                loadComponent: () => import('./pages/apps/loans/loan-detail/loan-detail-amortization/loan-detail-amortization.component').then(m => m.LoanDetailAmortizationComponent)
              },
              {
                path: 'cashflow',
                loadComponent: () => import('./pages/apps/loans/loan-detail/loan-detail-cashflow/loan-detail-cashflow.component').then(m => m.LoanDetailCashflowComponent)
              },
              {
                path: 'notes',
                loadComponent: () => import('./pages/apps/loans/loan-detail/loan-detail-notes/loan-detail-notes.component').then(m => m.LoanDetailNotesComponent)
              },
              {
                path: 'documents',
                loadComponent: () => import('./pages/apps/loans/loan-detail/loan-detail-documents/loan-detail-documents.component').then(m => m.LoanDetailDocumentsComponent)
              }
            ],
            data: {
              toolbarShadowEnabled: false,
              scrollDisabled: false,
              containerEnabled: true,
              breadcrumb: { dynamic: true, param: 'id' }
            }
          },
          {
            path: 'add',
            loadComponent: () => import('./pages/apps/loans/add-loan-page/add-loan-page.component').then(m => m.AddLoanPageComponent),
            data: {
              toolbarShadowEnabled: false,
              scrollDisabled: false,
              containerEnabled: true
            }
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./pages/apps/loans/add-loan-page/add-loan-page.component').then(m => m.AddLoanPageComponent),
            data: {
              toolbarShadowEnabled: false,
              scrollDisabled: false,
              containerEnabled: true,
              isEditMode: true
            }
          }
        ]
      },
      {
        path: 'service-providers',
        loadChildren: () => import('./pages/service-providers/service-providers.module').then(m => m.ServiceProvidersModule),
        data: {
          breadcrumb: 'breadcrumbs.serviceProviders'
        }
      },
      // Service Provider Workspace Routes
      {
        path: 'provider',
        canActivate: [ServiceProviderGuard],
        loadChildren: () => import('./pages/provider/provider.routes').then(m => m.PROVIDER_ROUTES)
      },
      // Tenant Workspace Routes — moved out of the Vex LayoutComponent.
      // Tenants get a dedicated mobile-first shell at the root level (see
      // the standalone block below). The placeholder remains here only so
      // the comment trail is preserved for future readers.

      // Syndic Workspace Routes — co-ownership building managers
      {
        path: 'syndic',
        canActivate: [SyndicWorkspaceGuard],
        loadChildren: () => import('./pages/syndic/syndic.routes').then(m => m.SYNDIC_ROUTES)
      },
      // Owner-side public-ish route for accepting a syndic-building invitation.
      // Authenticated-only (lives under the main LayoutComponent) but NOT gated
      // on workspace type — the receiving workspace is property-manager.
      {
        path: 'syndic-accept',
        loadComponent: () =>
          import('./pages/syndic/syndic-accept-invitation.component').then(
            (m) => m.SyndicAcceptInvitationComponent,
          ),
        data: { title: 'Accept syndic invitation' }
      },

      // Real Estate Agency Workspace Routes
      {
        path: 'agency',
        canActivate: [AgencyWorkspaceGuard],
        loadChildren: () => import('./pages/agency/agency.routes').then(m => m.AGENCY_ROUTES)
      },
      // Agency-side route for accepting an owner→agency mandate invitation.
      // Authenticated-only (lives under the main LayoutComponent) but NOT gated
      // on workspace type — the accepting member must be in their agency
      // workspace (enforced by the callable).
      {
        path: 'agency-accept',
        loadComponent: () =>
          import('./pages/agency/agency-accept-invitation.component').then(
            (m) => m.AgencyAcceptInvitationComponent,
          ),
        data: { title: 'Accept agency mandate' }
      },
      {
        path: 'admin',
        canActivate: [RoleGuard],
        data: {
          roles: ['superadmin', 'admin'],
          toolbarShadowEnabled: true
        },
        loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule)
      },
      {
        path: 'superadmin',
        redirectTo: 'admin',
        pathMatch: 'full'
      },
      {
        path: '**',
        loadComponent: () =>
          import('./pages/pages/errors/error-404/error-404.component').then(
            (m) => m.Error404Component
          )
      }
    ]
  },
  {
    path: '**',
    redirectTo: '/login'
  }
];
